/* Facturation — plans Noam via Stripe Checkout (sandbox réclamable). */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { CreditCard, Check, Loader2, Sparkles, ShieldCheck } from "lucide-react";

const fmtCHF = (cents) => `${(cents / 100).toLocaleString("fr-CH")} CHF`;

export default function Facturation() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/payments/plans")
      .then((r) => setPlans(r.data.plans || []))
      .catch((e) => notify.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, []);

  const checkout = async (lookup_key) => {
    setBusyKey(lookup_key);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      notify.error(fmtError(e));
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="facturation-root">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Pilotage</div>
        <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">Facturation & Abonnements</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Choisissez le plan adapté à votre profil. Paiement sécurisé par Stripe —
          abonnement mensuel résiliable à tout moment, modules débloqués automatiquement.
        </p>
      </div>

      <div className="noam-card p-3 flex items-center gap-2 text-xs text-zinc-400 border-[#378ADD]/20">
        <ShieldCheck className="w-4 h-4 text-[#378ADD] shrink-0" />
        Mode test actif — utilisez la carte <span className="font-mono text-zinc-200">4242 4242 4242 4242</span> (date future, CVC libre) pour simuler un paiement.
      </div>

      {loading ? (
        <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {plans.map((p) => {
            const highlight = p.lookup_key === "noam_integral_monthly";
            return (
              <div
                key={p.lookup_key}
                className={`noam-card p-5 flex flex-col ${highlight ? "border-[#378ADD]/50 relative" : ""}`}
                data-testid={`plan-card-${p.lookup_key}`}
              >
                {highlight && (
                  <span className="absolute -top-2.5 left-4 text-[10px] px-2 py-0.5 rounded-full bg-[#378ADD] text-white font-semibold inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Recommandé
                  </span>
                )}
                <div className="text-sm font-semibold">{p.name}</div>
                {p.audience && <div className="text-[10px] text-zinc-500 mt-0.5">{p.audience}</div>}
                <div className="mt-2">
                  <span className="text-3xl font-bold">{fmtCHF(p.amount)}</span>
                  <span className="text-xs text-zinc-500 ml-1">{p.interval === "month" ? "/ mois" : "one-shot"}</span>
                </div>
                <ul className="mt-4 space-y-2 text-xs text-zinc-300 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className="w-3.5 h-3.5 text-[#1D9E75] shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => checkout(p.lookup_key)}
                  disabled={busyKey !== null}
                  className={`mt-5 w-full justify-center ${highlight ? "btn-primary" : "btn-secondary"} text-xs`}
                  data-testid={`plan-checkout-${p.lookup_key}`}
                >
                  {busyKey === p.lookup_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                  {p.interval === "month" ? "S'abonner" : "Acheter"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
